/**
 * The B2B rate card: a zone-to-zone matrix plus the charges that go with it.
 *
 * B2B is not priced like B2C. There is no city/state/metro ladder — freight is quoted per kilo from
 * one zone to another, so the card is a 9x9 grid, and the rest of the money lives in a list of named
 * charges (min LR, ODA, COD, fuel and so on).
 *
 * The Billing Plans screen previously showed the B2C grid for both services, which meant a B2B card
 * was typed into boxes that do not exist for it and an existing B2B card opened blank — its numbers
 * are under `zoneMatrix`, and nothing on that form ever looked there.
 *
 * Deliberately the same field set and the same `config` shape as the merchant B2B pricing form, so a
 * plan's card can be copied onto a merchant untouched.
 */

/** Row = where it is picked up, column = where it is delivered. Same order the merchant form uses. */
export const B2B_ZONES = ['N1', 'N2', 'E', 'NE', 'W1', 'W2', 'S1', 'S2', 'Central'] as const;

export const B2B_OTHER_CHARGES: Array<{ key: string; label: string; calc: string }> = [
  { key: 'min_lr_charge', label: 'Minimum LR charge', calc: 'Fixed' },
  { key: 'min_charge_weight', label: 'Minimum charge weight', calc: 'Fixed (kg)' },
  { key: 'processing_charge', label: 'Processing charge', calc: 'Fixed per LR' },
  { key: 'divisor', label: 'Divisor', calc: 'Volumetric divisor' },
  { key: 'pod_charge', label: 'POD charge', calc: 'Fixed amount' },
  { key: 'fuel_charge_percent', label: 'Fuel charge', calc: '%' },
  { key: 'rov_carrier', label: 'ROV — carrier risk', calc: '% of invoice value, or min' },
  { key: 'rov_owner', label: 'ROV — owner risk', calc: '% of invoice value, or min' },
  { key: 'oda_charge_amt', label: 'ODA charge', calc: 'Min, if the unit charge is per kg' },
  { key: 'appointment_charge_amt', label: 'Appointment charge', calc: 'Per kg or min, when the LR has an appointment' },
  { key: 'cod_charge_amt', label: 'COD charge', calc: 'Fixed' },
  { key: 'other_handling_charge_amt', label: 'Other handling charge', calc: 'Per kg or min, from' },
  { key: 'green_tax_amt', label: 'Green tax', calc: 'Fixed' },
  { key: 'demurrage_charge_amt', label: 'Demurrage', calc: 'Fixed or per day' },
  { key: 'cheque_charge_amt', label: 'Cheque charge', calc: 'Fixed' },
  { key: 'fm_charges', label: 'FM charges', calc: 'Fixed or per kg' },
  { key: 'to_pay_handling_charges', label: 'To-pay handling', calc: 'Fixed' },
];

/** One "Other charges" row. `minKg` is used by other handling only: the weight it starts at. */
export type B2bChargeCell = { unit?: number | string; min?: number | string; minKg?: number | string };

/**
 * What each box of a charge row holds, shown inside the box so "3" reads as "₹3 per kg" and "600" as
 * "₹600 minimum". `higher` rows are billed as unit × kg (or % of invoice) or the minimum, whichever
 * is higher; the rest are a single fixed figure, typed in either box.
 */
export const B2B_CHARGE_UNITS: Record<string, { unit: string; min: string; higher?: 'kg' | 'invoice' | 'day' }> = {
  min_lr_charge: { unit: '₹ / LR', min: '₹ / LR' },
  min_charge_weight: { unit: 'kg', min: 'kg' },
  processing_charge: { unit: '₹ / LR', min: '₹ / LR' },
  divisor: { unit: 'divisor', min: 'divisor' },
  pod_charge: { unit: '₹ fixed', min: '₹ fixed' },
  fuel_charge_percent: { unit: '% freight', min: '% freight' },
  rov_carrier: { unit: '% invoice', min: '₹ min amt', higher: 'invoice' },
  rov_owner: { unit: '% invoice', min: '₹ min amt', higher: 'invoice' },
  oda_charge_amt: { unit: '₹ per kg', min: '₹ min amt', higher: 'kg' },
  appointment_charge_amt: { unit: '₹ per kg', min: '₹ min amt', higher: 'kg' },
  cod_charge_amt: { unit: '₹ fixed', min: '₹ fixed' },
  other_handling_charge_amt: { unit: '₹ per kg', min: '₹ min amt', higher: 'kg' },
  green_tax_amt: { unit: '₹ fixed', min: '₹ fixed' },
  demurrage_charge_amt: { unit: '₹ per day', min: '₹ min amt', higher: 'day' },
  cheque_charge_amt: { unit: '₹ fixed', min: '₹ fixed' },
  fm_charges: { unit: '₹ per kg', min: '₹ min amt', higher: 'kg' },
  to_pay_handling_charges: { unit: '₹ fixed', min: '₹ fixed' },
};

const filled = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '' && String(v).trim().toUpperCase() !== 'NA';

/** "₹3 per kg or ₹600 min amount, whichever is higher — on shipments of 70 kg and above". */
export function chargeRule(key: string, cell: B2bChargeCell | undefined): string | null {
  const u = B2B_CHARGE_UNITS[key];
  if (!u?.higher || !cell) return null;
  const per = u.higher === 'kg' ? 'per kg' : u.higher === 'day' ? 'per day' : 'of invoice value';
  const unitTxt = filled(cell.unit) ? (u.higher === 'invoice' ? `${cell.unit}% ${per}` : `₹${cell.unit} ${per}`) : null;
  const minTxt = filled(cell.min) ? `₹${cell.min} min amount` : null;
  if (!unitTxt && !minTxt) return null;
  let txt = unitTxt && minTxt ? `${unitTxt} or ${minTxt}, whichever is higher` : (unitTxt ?? minTxt)!;
  if (key === 'appointment_charge_amt') txt += ' — only when the LR has an appointment (carrier appointment ID or third party)';
  if (key === 'other_handling_charge_amt') {
    txt += filled(cell.minKg) ? ` — on shipments of ${cell.minKg} kg and above` : ' — not billed until a minimum weight is set';
  }
  return txt;
}

/** A charge box with what it holds written inside it, on the right. */
export function ChargeInput({ value, onChange, suffix, placeholder, className = '' }: {
  value: any; onChange: (v: string) => void; suffix?: string; placeholder?: string; className?: string;
}) {
  return (
    <div className="relative">
      <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`w-full px-2 py-1 border rounded ${suffix ? 'pr-[4.5rem]' : ''} ${className}`} />
      {suffix && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 whitespace-nowrap">{suffix}</span>
      )}
    </div>
  );
}

/** The "Min [ ] KG" box for other handling: the weight from which the charge applies. */
export function MinKgInput({ value, onChange }: { value: any; onChange: (v: string) => void }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      Min
      <input type="number" min={0} step="0.01" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="w-16 px-1.5 py-1 border border-slate-300 rounded text-xs text-center" placeholder="kg" />
      KG
    </span>
  );
}

export interface B2bConfig {
  zoneMatrix: Record<string, Record<string, number | undefined>>;
  otherCharges: Record<string, B2bChargeCell>;
  stateCharges?: Array<{ stateId?: number; charge?: number }>;
  cityCharges?: Array<{ cityId?: number; cityName?: string; charge?: number }>;
}

export const blankB2bConfig: B2bConfig = { zoneMatrix: {}, otherCharges: {}, stateCharges: [], cityCharges: [] };

/** A B2B card counts as filled only when the MATRIX has a rate — charges alone price nothing. */
export function hasAnyB2bRate(cfg: B2bConfig): boolean {
  return Object.values(cfg?.zoneMatrix ?? {}).some((row) =>
    Object.values(row ?? {}).some((v) => v !== undefined && v !== null && String(v) !== '' && Number(v) > 0));
}

export function B2bRateCardFields({ cfg, onChange }: { cfg: B2bConfig; onChange: (next: B2bConfig) => void }) {
  const setCell = (from: string, to: string, v: string) => {
    const row = { ...(cfg.zoneMatrix[from] ?? {}) };
    if (v === '') delete row[to]; else row[to] = Number(v);
    onChange({ ...cfg, zoneMatrix: { ...cfg.zoneMatrix, [from]: row } });
  };
  const setCharge = (key: string, part: 'unit' | 'min' | 'minKg', v: string) =>
    onChange({ ...cfg, otherCharges: { ...cfg.otherCharges, [key]: { ...(cfg.otherCharges[key] ?? {}), [part]: v } } });

  /** Fill a whole row from its first value — most cards are "this much anywhere, more to the NE". */
  const fillRow = (from: string) => {
    const row = cfg.zoneMatrix[from] ?? {};
    const first = B2B_ZONES.map((z) => row[z]).find((v) => v !== undefined && String(v) !== '');
    if (first === undefined) return;
    const filled: Record<string, number> = {};
    for (const z of B2B_ZONES) filled[z] = Number(first);
    onChange({ ...cfg, zoneMatrix: { ...cfg.zoneMatrix, [from]: filled } });
  };

  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-900">Zone rates (₹ per kg)</h4>
          <span className="text-[11px] text-slate-500">Row = pickup zone, column = delivery zone</span>
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left text-slate-500 sticky left-0 bg-white">From \ To</th>
                {B2B_ZONES.map((z) => <th key={z} className="px-1 py-1 text-slate-500 text-center min-w-[58px]">{z}</th>)}
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {B2B_ZONES.map((from) => (
                <tr key={from}>
                  <td className="px-2 py-1 font-medium text-slate-700 sticky left-0 bg-white">{from}</td>
                  {B2B_ZONES.map((to) => (
                    <td key={to} className="px-0.5 py-0.5">
                      <input
                        type="number" step="0.01"
                        value={cfg.zoneMatrix[from]?.[to] ?? ''}
                        onChange={(e) => setCell(from, to, e.target.value)}
                        className={`w-[58px] px-1.5 py-1 border rounded text-xs text-center ${from === to ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`}
                      />
                    </td>
                  ))}
                  <td className="px-1">
                    <button type="button" onClick={() => fillRow(from)} title={`Copy the first value across the ${from} row`}
                      className="text-[10px] text-indigo-600 hover:underline whitespace-nowrap">fill →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          The green diagonal is within the same zone. Type the first value in a row and press <b>fill →</b> to copy it
          across, then change the ones that differ.
        </p>
      </div>

      <div className="border border-slate-200 rounded-lg p-3">
        <h4 className="text-sm font-semibold text-slate-900 mb-2">Other charges</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 text-left">
                <th className="py-1 pr-2">Charge</th>
                <th className="py-1 pr-2">How it is worked out</th>
                <th className="py-1 pr-2 w-36">Unit / rate</th>
                <th className="py-1 w-36">Minimum</th>
              </tr>
            </thead>
            <tbody>
              {B2B_OTHER_CHARGES.map((c) => (
                <tr key={c.key} className="border-t border-slate-50">
                  <td className="py-1 pr-2 text-slate-700">{c.label}</td>
                  <td className="py-1 pr-2 text-slate-400">
                    <div className="flex items-center gap-1.5">
                      {c.calc}
                      {c.key === 'other_handling_charge_amt' && (
                        <MinKgInput value={cfg.otherCharges[c.key]?.minKg} onChange={(v) => setCharge(c.key, 'minKg', v)} />
                      )}
                    </div>
                    {chargeRule(c.key, cfg.otherCharges[c.key]) && (
                      <div className="text-[10px] text-indigo-600 mt-0.5">{chargeRule(c.key, cfg.otherCharges[c.key])}</div>
                    )}
                  </td>
                  <td className="py-1 pr-2">
                    <ChargeInput value={cfg.otherCharges[c.key]?.unit} onChange={(v) => setCharge(c.key, 'unit', v)}
                      suffix={B2B_CHARGE_UNITS[c.key]?.unit} className="border-slate-200 text-xs" placeholder="—" />
                  </td>
                  <td className="py-1">
                    <ChargeInput value={cfg.otherCharges[c.key]?.min} onChange={(v) => setCharge(c.key, 'min', v)}
                      suffix={B2B_CHARGE_UNITS[c.key]?.min} className="border-slate-200 text-xs" placeholder="—" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Leave a charge blank if it does not apply. <b>NA</b> is accepted where a charge exists but has no minimum —
          it is stored exactly as the merchant rate card stores it.
        </p>
      </div>
    </div>
  );
}
